import React from "react";
import { User } from "../api/types.ts";
import { AnimatedLoading } from "../components/widgets//AnimatedLoading.tsx";
import { useDialog } from "../components/DialogManager.tsx";
import { Button } from "../components/widgets/Button.tsx";
import { HiOutlineArrowNarrowLeft } from "react-icons/hi";

interface ProfilePageProps {
  profile?: User;
  profileLoading?: boolean;
  profileError: Error | null;
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  profile,
  profileLoading,
  profileError,
}) => {
  const { openDialog } = useDialog();

  if (profileLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <AnimatedLoading />
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Error loading profile: {profileError.message}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          No profile data available.
        </div>
      </div>
    );
  }

  const purposeLabels: Record<string, string> = {
    improve_fitness: "Improve Fitness",
    maintain_fitness: "Maintain Fitness",
    general_tracking: "General Tracking",
    other: "Other",
  };

  return (
    <div className="space-y-2 mx-auto max-w-2xl px-4 py-8">
      <button
        className="flex flex-row items-center gap-2 text-primary hover:text-primary-dark transition-colors"
        onClick={() => window.history.back()}
      >
        <HiOutlineArrowNarrowLeft />
        Go Back
      </button>
      <div className="container mx-auto max-w-2xl">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2>Your Profile</h2>
            <Button
              variant="primary"
              onClick={() => openDialog("profile-settings")}
            >
              Edit Profile
            </Button>
          </div>

          <div className="space-y-4">
            <h4 className="border-b pb-2 text-primary">General</h4>

            <div className="pb-4">
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className=" text-gray-700">{profile.email}</p>
            </div>

            <div className="pb-4">
              <label className="text-sm font-medium text-gray-500">
                Display Name
              </label>
              <p className=" text-gray-700">{profile.name || "Not set"}</p>
            </div>

            <h4 className="border-b pb-2 text-primary">Fitness</h4>

            <div className="pb-4">
              <label className="text-sm font-medium text-gray-500">
                General Goal
              </label>
              <p className=" text-gray-700">
                {profile.purpose
                  ? purposeLabels[profile.purpose] || profile.purpose
                  : "Not set"}
              </p>
            </div>

            <h4 className="border-b pb-2 text-primary">Preferences</h4>
            <div className="pb-4">
              <label className="text-sm font-medium text-gray-500">
                Resistance Unit
              </label>
              <p className=" text-gray-700">
                {profile.preferred_resistance_unit
                  ? profile.preferred_resistance_unit === "kg"
                    ? "Kilograms (kg)"
                    : "Pounds (lbs)"
                  : "Not set"}
              </p>
            </div>

            <div className="pb-4">
              <label className="text-sm font-medium text-gray-500">
                Time Format
              </label>
              <p className=" text-gray-700">
                {profile.preferred_time_format
                  ? profile.preferred_time_format === "24h"
                    ? "24-hour"
                    : "12-hour (AM/PM)"
                  : "Not set"}
              </p>
            </div>

            <div className="pb-4">
              <label className="text-sm font-medium text-gray-500">
                Visibility
              </label>
              <p className=" text-gray-700">
                {profile.preferred_visibility
                  ? profile.preferred_visibility === "private"
                    ? "Keep my workouts private"
                    : "Share my workouts to the activity feed"
                  : "Not set"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ProfilePage };
